import React, { ChangeEvent, ReactNode, useState } from 'react';
import { useClasses } from 'app/utils/css';
import { inputBoxshadowStyles } from 'app/components/presentation/forms/styles';

const SIZE_UNIT = 24; // px
const PADDING   = 15;

export const BASE_HEIGHT         = SIZE_UNIT + PADDING * 2;
export const BASE_HEIGHT_PX      = `${BASE_HEIGHT}px`;

export const ICON_SIZE_NORMAL_PX = `${SIZE_UNIT}px`;
export const ICON_SIZE_SMALL_PX  = `${SIZE_UNIT / 2}px`;

export enum IconSize {
  Normal,
  Small,
}

interface IProps {
  ariaLabel?: string;
  icon?: ReactNode;
  iconSize?: IconSize;
  onChange: (value: string) => void;
  placeholder?: string;
  value: string;
}

const Input = ({
  ariaLabel,
  icon,
  iconSize = IconSize.Normal,
  onChange,
  placeholder,
  value,
}: IProps) => {
  const [hasFocus, setHasFocus] = useState(false);
  const classes = useClasses({
    inputContainer: [
      {
        flexGrow: 1,
        minHeight: BASE_HEIGHT_PX,
        position: 'relative',
        display: 'flex',
        alignItems: 'stretch',
        background: '#fff',
        borderRadius: '2px',
        zIndex: 0,
      },
      inputBoxshadowStyles(hasFocus),
    ],

    input: [
      {
        position: 'relative',
        appearance: 'none',
        background: 'transparent',
        boxSizing: 'border-box',
        outline: 'none',
        margin: 0,
        padding: `0 ${PADDING}px`,
        borderRadius: '2px',
        border: 'none',
        flexGrow: 1,
        zIndex: 1,
        '::-ms-clear': {
          display: 'none',
        },
      },
      !!icon && {
        paddingRight: 0,
        marginRight: BASE_HEIGHT_PX,
      },
    ],

    iconContainer: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      right: 0,
      width: BASE_HEIGHT_PX,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },

    iconSize: [
      {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      iconSize === IconSize.Normal && { width: ICON_SIZE_NORMAL_PX, height: ICON_SIZE_NORMAL_PX },
      iconSize === IconSize.Small && { width: ICON_SIZE_SMALL_PX, height: ICON_SIZE_SMALL_PX },
    ],
  });

  const inputOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  const onBlur = () => {
    setHasFocus(false);
  };

  const onFocus = () => {
    setHasFocus(true);
  };

  return (
    <div className={classes.inputContainer}>
      <input
        aria-label={ariaLabel}
        className={classes.input}
        onChange={inputOnChange}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        value={value}
      />
      {icon && (
        <div className={classes.iconContainer}>
          <div className={classes.iconSize}>
            {icon}
          </div>
        </div>
      )}
    </div>
  );
};

export default Input;
